import React from 'react';
import { Box, CircularProgress } from '@mui/material';
import { Navigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext.tsx';
import { toastError } from '../shared/ui/toast.ts';

type Props = {
  children: JSX.Element;
  requiredPermissions?: string[];
  requiredRoles?: string[];
};

// SUPERADMIN-12 (MA-28c): a missing role/permission used to bounce the operator to /dashboard
// SILENTLY — indistinguishable from a broken link. This wrapper fires a one-line notice before the
// redirect so the swap is visible and explains itself. The toast lives in an effect (never during
// render) to respect the strict hooks-at-top rule.
const RedirectWithNotice: React.FC<{ to: string; message: string }> = ({ to, message }) => {
  React.useEffect(() => {
    toastError(message);
  }, [message]);
  return <Navigate to={to} replace />;
};

const PrivateRoute: React.FC<Props> = ({
  children,
  requiredPermissions = [],
  requiredRoles = [],
}) => {
  const { loading, isAuthenticated, user, hasMenuPermission } = useAuth();

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', mt: 5 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (!isAuthenticated() || !user?.internalUser) {
    return <Navigate to="/login" replace />;
  }

  const normalizedRoles = (user.roles || []).map((role) =>
    role.toUpperCase().replace(/^ROLE_/, ''),
  );
  const missingRole =
    requiredRoles.length > 0 &&
    !requiredRoles.some((role) => normalizedRoles.includes(role.toUpperCase()));

  if (missingRole) {
    return (
      <RedirectWithNotice
        to="/dashboard"
        message="You don't have the required role for that page — returned to Overview."
      />
    );
  }

  const missingPermission = requiredPermissions.some((token) => {
    const [menuKey, actionCode = 'VIEW'] = token.split(':');
    return !hasMenuPermission(menuKey, actionCode);
  });

  if (missingPermission) {
    return (
      <RedirectWithNotice
        to="/dashboard"
        message="You don't have permission for that page — returned to Overview."
      />
    );
  }

  return children;
};

export default PrivateRoute;
